// v49.54 — useMfaStatus hook.
// Shared by TwoFactorAuthCard (Settings) and MfaChallenge (sign-in
// step-up). Tracks the user's verified TOTP factor + the current
// session's Authenticator Assurance Level (AAL1 vs AAL2). Re-fetches
// on auth state changes so enrolment + step-up reflect immediately.

import { useCallback, useEffect, useState } from "react";
import { LABOURCORE_API } from "@/lib/api";

type Aal = "aal1" | "aal2" | null;

export interface VerifiedFactor {
  id:            string;
  friendly_name: string | null;
  created_at:    string;
}

export interface MfaStatus {
  loading:        boolean;
  currentLevel:   Aal;
  nextLevel:      Aal;
  verifiedFactor: VerifiedFactor | null;
  /* Convenience: a stepUp is needed when the user has a verified
     factor AND current level is still aal1. */
  needsStepUp:    boolean;
  refresh:        () => Promise<void>;
}

export function useMfaStatus(): MfaStatus {
  const [loading, setLoading] = useState(true);
  const [currentLevel, setCurrentLevel] = useState<Aal>(null);
  const [nextLevel, setNextLevel]       = useState<Aal>(null);
  const [verifiedFactor, setFactor]     = useState<VerifiedFactor | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const aal = await LABOURCORE_API.auth.mfa.getAal();
      setCurrentLevel((aal.currentLevel as Aal) ?? null);
      setNextLevel((aal.nextLevel as Aal) ?? null);
      const factors = await LABOURCORE_API.auth.mfa.listFactors();
      const totp = Array.isArray(factors.totp) ? factors.totp : [];
      const verified = totp.find(f => f.status === "verified") || null;
      setFactor(verified ? {
        id:            verified.id,
        friendly_name: verified.friendly_name ?? null,
        created_at:    verified.created_at,
      } : null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  /* Re-fetch on auth state changes so enrol / step-up land instantly. */
  useEffect(() => {
    const sub = LABOURCORE_API.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "MFA_CHALLENGE_VERIFIED" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        void refresh();
      }
    });
    return () => { try { sub.unsubscribe(); } catch { /* ignore */ } };
  }, [refresh]);

  return {
    loading,
    currentLevel,
    nextLevel,
    verifiedFactor,
    needsStepUp: !!verifiedFactor && currentLevel === "aal1" && nextLevel === "aal2",
    refresh,
  };
}
